/**
 * CCITT Group 4 (T.6) encoder for black-and-white pictures. PDF readers decode it natively (/CCITTFaxDecode),
 * and for text pages it is about ten times smaller than the same page saved as JPEG.
 */
const WHITE_T: string[] = ["00110101","000111","0111","1000","1011","1100","1110","1111","10011","10100","00111","01000","001000","000011","110100","110101","101010","101011","0100111","0001100","0001000","0010111","0000011","0000100","0101000","0101011","0010011","0100100","0011000","00000010","00000011","00011010","00011011","00010010","00010011","00010100","00010101","00010110","00010111","00101000","00101001","00101010","00101011","00101100","00101101","00000100","00000101","00001010","00001011","01010010","01010011","01010100","01010101","00100100","00100101","01011000","01011001","01011010","01011011","01001010","01001011","00110010","00110011","00110100"];
const BLACK_T: string[] = ["0000110111","010","11","10","011","0011","0010","00011","000101","000100","0000100","0000101","0000111","00000100","00000111","000011000","0000010111","0000011000","0000001000","00001100111","00001101000","00001101100","00000110111","00000101000","00000010111","00000011000","000011001010","000011001011","000011001100","000011001101","000001101000","000001101001","000001101010","000001101011","000011010010","000011010011","000011010100","000011010101","000011010110","000011010111","000001101100","000001101101","000011011010","000011011011","000001010100","000001010101","000001010110","000001010111","000001100100","000001100101","000001010010","000001010011","000000100100","000000110111","000000111000","000000100111","000000101000","000001011000","000001011001","000000101011","000000101100","000001011010","000001100110","000001100111"];
const WHITE_M: string[] = ["11011","10010","010111","0110111","00110110","00110111","01100100","01100101","01101000","01100111","011001100","011001101","011010010","011010011","011010100","011010101","011010110","011010111","011011000","011011001","011011010","011011011","010011000","010011001","010011010","011000","010011011","00000001000","00000001100","00000001101","000000010010","000000010011","000000010100","000000010101","000000010110","000000010111","000000011100","000000011101","000000011110","000000011111"];
const BLACK_M: string[] = ["0000001111","000011001000","000011001001","000001011011","000000110011","000000110100","000000110101","0000001101100","0000001101101","0000001001010","0000001001011","0000001001100","0000001001101","0000001110010","0000001110011","0000001110100","0000001110101","0000001110110","0000001110111","0000001010010","0000001010011","0000001010100","0000001010101","0000001011010","0000001011011","0000001100100","0000001100101","00000001000","00000001100","00000001101","000000010010","000000010011","000000010100","000000010101","000000010110","000000010111","000000011100","000000011101","000000011110","000000011111"];

class BitWriter {
  private out: number[] = [];
  private cur = 0;
  private n = 0;
  put(code: string) {
    for (let i = 0; i < code.length; i++) {
      this.cur = (this.cur << 1) | (code.charCodeAt(i) - 48);
      if (++this.n === 8) { this.out.push(this.cur); this.cur = 0; this.n = 0; }
    }
  }
  finish(): Uint8Array {
    if (this.n) this.out.push(this.cur << (8 - this.n));
    return Uint8Array.from(this.out);
  }
}

function putRun(bw: BitWriter, len: number, black: boolean) {
  const M = black ? BLACK_M : WHITE_M, T = black ? BLACK_T : WHITE_T;
  while (len >= 2560) { bw.put(M[27 + 12]); len -= 2560; }
  if (len >= 64) {
    const k = Math.floor(len / 64);
    bw.put(k <= 27 ? M[k - 1] : M[27 + (k - 28)]);
    len -= k * 64;
  }
  bw.put(T[len]);
}

/** `pixels` holds one byte per pixel, row after row; non-zero is black. */
export function encodeG4(pixels: Uint8Array, width: number, height: number): Uint8Array {
  const bw = new BitWriter();
  let ref: Uint8Array = new Uint8Array(width); // all white
  for (let y = 0; y < height; y++) {
    const row = pixels.subarray(y * width, (y + 1) * width);
    const at = (r: Uint8Array, x: number) => (x < 0 ? 0 : r[x] ? 1 : 0);
    let a0 = -1, color = 0;
    while (a0 < width) {
      let a1 = a0 + 1;
      while (a1 < width && at(row, a1) === color) a1++;
      let b1 = a0 + 1;
      while (b1 < width && !(at(ref, b1) !== color && at(ref, b1 - 1) === color)) b1++;
      let b2 = b1 + 1;
      while (b2 < width && at(ref, b2) !== color) b2++;
      if (b1 >= width) b2 = width; else if (b2 > width) b2 = width;
      if (b2 < a1) { bw.put("0001"); a0 = b2; continue; }
      const d = a1 - b1;
      if (d >= -3 && d <= 3) {
        bw.put(["0000010", "000010", "010", "1", "011", "000011", "0000011"][d + 3]);
        a0 = a1; color ^= 1;
        continue;
      }
      let a2 = a1 + 1;
      while (a2 < width && at(row, a2) !== color) a2++;
      if (a1 >= width) a2 = width; else if (a2 > width) a2 = width;
      bw.put("001");
      putRun(bw, a1 - Math.max(a0, 0), color === 1);
      putRun(bw, a2 - a1, color === 0);
      a0 = a2;
    }
    ref = row;
  }
  bw.put("000000000001000000000001");
  return bw.finish();
}
